import { type ReactElement, useId } from 'react';

import { faCheck } from '@awesome.me/kit-379289146f/icons/classic/solid';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Chip,
  FormControl,
  type FormControlProps,
  type FormHelperTextProps,
  type InputLabelProps,
  ListItemIcon,
  ListItemText,
  MenuItem,
  type MenuItemProps,
  type SelectProps as MuiSelectProps,
  Stack,
} from '@mui/material';

import {
  StyledFormHelperText,
  StyledInputLabel,
  StyledSelect,
} from './components';

export type SelectOption<
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
  Label extends number | string,
  Value extends NonNullable<MenuItemProps['value']>,
> = Record<LabelKey, Label> &
  Record<ValueKey, Value> &
  Record<PropertyKey, unknown> & {
    disabled?: boolean;
  };

type DefaultLabelValueKeys<
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
> = {
  label: LabelKey;
  value: ValueKey;
};

export type AccessSelectProps<
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
  Label extends number | string,
  Value extends NonNullable<MenuItemProps['value']>,
> = {
  helperText?: string;
  FormControlProps?: FormControlProps;
  InputLabelProps?: InputLabelProps;
  FormHelperTextProps?: FormHelperTextProps;
  options: Array<SelectOption<LabelKey, ValueKey, Label, Value>>;
  optionKeys: DefaultLabelValueKeys<LabelKey, ValueKey>;
} & MuiSelectProps;

export const AccessSelect = <
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
  Label extends number | string,
  Value extends NonNullable<MenuItemProps['value']>,
>({
  FormControlProps,
  InputLabelProps,
  FormHelperTextProps,
  options,
  optionKeys,
  placeholder,
  helperText,
  label,
  ...props
}: AccessSelectProps<LabelKey, ValueKey, Label, Value>): ReactElement => {
  const componentId = useId();

  const createChipColor = (status: string) => {
    switch (status) {
      case 'User':
        return 'primary';
      case 'Modified':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <FormControl fullWidth {...FormControlProps}>
      {label && (
        <StyledInputLabel
          shrink
          required={props.required}
          htmlFor={props.id ?? componentId}
          {...InputLabelProps}
        >
          {label}
        </StyledInputLabel>
      )}

      <StyledSelect
        id={componentId}
        displayEmpty={!props.value}
        renderValue={(selected) => {
          if (
            selected === undefined ||
            (typeof selected === 'string' && selected.length === 0)
          )
            return placeholder;

          const selectedValue = options.find(
            (el) => el[optionKeys.value] === selected,
          );

          return selectedValue?.[optionKeys.label];
        }}
        {...props}
      >
        {options.map((option) => {
          const label = option[optionKeys.label];
          const status = option.status as string;
          const value = option[optionKeys.value];
          const isDisabled = Boolean(option.disabled);
          const isSelected = value === props.value;

          if (option.type === 'category') {
            return (
              <MenuItem key={value} value={value} disabled divider>
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: 15,
                    fontWeight: 'bold',
                  }}
                >
                  {label}
                </ListItemText>
              </MenuItem>
            );
          }

          if (option.type === 'options') {
            return (
              <MenuItem key={value} value={value} disabled={isDisabled}>
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: 13,
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Chip
                      label={status}
                      color={createChipColor(status)}
                      size="small"
                    />
                    {label}
                  </Stack>
                </ListItemText>
                {isSelected && (
                  <ListItemIcon
                    sx={{
                      justifyContent: 'flex-end',
                    }}
                  >
                    <FontAwesomeIcon
                      color="#73808B"
                      size="lg"
                      icon={faCheck as IconProp}
                    />
                  </ListItemIcon>
                )}
              </MenuItem>
            );
          }

          return null;
        })}
      </StyledSelect>

      {helperText && (
        <StyledFormHelperText error={props.error} {...FormHelperTextProps}>
          {helperText}
        </StyledFormHelperText>
      )}
    </FormControl>
  );
};
